import * as React from "react";

import { useLocation, useNavigate } from "@remix-run/react";
import { useQueryClient } from "@tanstack/react-query";

import { useCloseOnboardingChecklist } from "~/hooks/queries/use-onboarding-checklist";
import { useUserDetails } from "~/hooks/queries/use-user-details";
import { useIsMobile } from "~/hooks/use-mobile";
import { trackEvent } from "~/lib/analytics";
import { getBrowserEnvVar } from "~/lib/env.shared";
import { useIsPractitioner } from "~/lib/user";
import { useTour } from "~/features/onboarding";

import { ITEMS, type OnboardingItemId } from "../items";
import type { OnboardingChecklistStatus } from "../types";
import {
    OnboardingChecklistWidget,
    type OnboardingChecklistMode,
} from "./onboarding-checklist-widget";

const COLLAPSED_STORAGE_KEY = "onboarding-checklist-collapsed";
const JUST_DONE_MS = 700;
const REQUIRED_ITEM_IDS: OnboardingItemId[] = ITEMS.filter(
    (item) => !item.optional,
).map((item) => item.id);
const ITEM_ROUTES: Record<OnboardingItemId, string> = {
    help: "/help",
    // AIDEV-NOTE: "Add a session" → the top-level record/upload/invite hub.
    session: "/invite",
    invite: "/practitioner/clients",
    consult: "/practitioner/sessions",
    chat: "/practitioner/sessions",
};

// AIDEV-NOTE: Navigate-then-tour. Some items open a page then coach-mark an
// element there. The tour system does NOT wait for the target, so we deep-link
// with ?onboardingTour=<key>, then poll for the target and startTour once it's
// in the DOM (see the launcher effect). Desktop-only.
const TOUR_PARAM = "onboardingTour";
const TOUR_LAUNCH: Record<string, { tourId: string; target: string }> = {
    consult: {
        tourId: "consult-callout",
        target: "[data-tour-id='consult-button']",
    },
    chat: {
        tourId: "consultant-chat-callout",
        target: "[data-tour-id='session-consultant-chat-input']",
    },
};

export function shouldShowOnboardingChecklist(
    pathname: string,
    search = "",
): boolean {
    // AIDEV-NOTE: The persisted demo tour owns onboarding=1 session/consultation screens.
    const initialOnboarding =
        new URLSearchParams(search).get("onboarding") === "1";
    // AIDEV-NOTE: Never show the guided-onboarding checklist on admin tooling
    // (/admin/*) - those are internal operator screens, not a practitioner's
    // first-run experience, and the fixed widget overlaps their UI.
    const isAdminRoute =
        pathname === "/admin" || pathname.startsWith("/admin/");
    // AIDEV-NOTE: The recording splitter is a dense, full-width editing surface
    // (waveform, drag handles, per-session labelling). A fixed widget in the
    // corner competes with it for attention at exactly the moment the user is
    // concentrating on boundaries, and testers read it as noise. Suppressed for
    // the duration of that flow; it returns on every other screen.
    const isRecordingSplit = pathname.startsWith("/sessions/new/split");
    // AIDEV-NOTE: Resources is reading and practice, not first-run setup. The whole tree is
    // suppressed (not just the resource page) because the fixed widget sits exactly where the
    // mobile practice dock and the desktop "next resource" CTA live, so it competes with the
    // primary action on every screen in the area — and it reads as noise mid-meditation.
    const isResources =
        pathname === "/resources" || pathname.startsWith("/resources/");
    return (
        !initialOnboarding &&
        !isAdminRoute &&
        !isRecordingSplit &&
        !isResources &&
        pathname !== "/onboarding" &&
        !pathname.startsWith("/onboarding/")
    );
}

function canUseSessionStorage(): boolean {
    if (typeof window === "undefined") return false;

    try {
        const testKey = "__onboarding_checklist_test__";
        window.sessionStorage.setItem(testKey, "1");
        window.sessionStorage.removeItem(testKey);
        return true;
    } catch {
        return false;
    }
}

function readStoredMode(
    defaultMode: OnboardingChecklistMode,
): OnboardingChecklistMode {
    if (!canUseSessionStorage()) return defaultMode;

    return window.sessionStorage.getItem(COLLAPSED_STORAGE_KEY) === "true"
        ? "collapsed"
        : "expanded";
}

function writeStoredMode(mode: OnboardingChecklistMode): void {
    if (!canUseSessionStorage()) return;

    window.sessionStorage.setItem(
        COLLAPSED_STORAGE_KEY,
        mode === "collapsed" ? "true" : "false",
    );
}

function getCompletedIds(
    status: OnboardingChecklistStatus,
): Set<OnboardingItemId> {
    if (!status.eligible) return new Set();

    return new Set(
        status.items
            .filter((item) => item.done)
            .map((item) => item.key as OnboardingItemId),
    );
}

export function OnboardingChecklistContainer({
    narrow,
    rightOffset,
}: {
    narrow?: boolean;
    /** Extra right offset so the widget clears an open right sidebar. */
    rightOffset?: number | string;
}) {
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    const isMobile = useIsMobile();
    const isNarrow = narrow ?? isMobile;
    const { isPractitioner } = useIsPractitioner();
    const { startTour } = useTour();
    const userDetailsQuery = useUserDetails();
    const closeMutation = useCloseOnboardingChecklist();
    const [hidden, setHidden] = React.useState(false);
    const [mode, setMode] =
        React.useState<OnboardingChecklistMode>("collapsed");
    const [justDoneId, setJustDoneId] = React.useState<OnboardingItemId | null>(
        null,
    );
    const previousCompletedIdsRef = React.useRef<Set<OnboardingItemId> | null>(
        null,
    );
    const trackedCompletedRef = React.useRef(false);
    const statusRef = React.useRef<OnboardingChecklistStatus | undefined>(
        undefined,
    );

    React.useEffect(() => {
        const defaultMode = isNarrow ? "collapsed" : "expanded";
        setMode(readStoredMode(defaultMode));
    }, [isNarrow]);

    const status = userDetailsQuery.data?.onboardingChecklist;
    statusRef.current = status;

    // AIDEV-NOTE: Refreshes the whole user payload; accepted overhead for onboarding-eligible practitioners.
    const didInitialReconcileRef = React.useRef(false);
    React.useEffect(() => {
        if (!isPractitioner) return;
        const currentStatus = statusRef.current;
        if (
            didInitialReconcileRef.current &&
            currentStatus &&
            !currentStatus.eligible
        ) {
            return;
        }
        didInitialReconcileRef.current = true;
        void queryClient.invalidateQueries({ queryKey: ["user"] });
    }, [location.pathname, isPractitioner, queryClient]);

    // AIDEV-NOTE: After navigating with ?onboardingTour=<key>, poll for the tour's
    // target element (it lives on the destination page and may mount late), then
    // start the tour and strip the param so it doesn't re-fire.
    React.useEffect(() => {
        if (isNarrow) return;
        const params = new URLSearchParams(location.search);
        const key = params.get(TOUR_PARAM);
        if (!key) return;
        const launch = TOUR_LAUNCH[key];
        if (!launch) return;

        let cancelled = false;
        let attempts = 0;
        const maxAttempts = 80; // ~8s at 100ms

        const stripParam = () => {
            const next = new URLSearchParams(location.search);
            next.delete(TOUR_PARAM);
            navigate(
                {
                    pathname: location.pathname,
                    search: next.toString(),
                },
                { replace: true },
            );
        };

        const tick = () => {
            if (cancelled) return;
            const el = document.querySelector<HTMLElement>(launch.target);
            if (el && el.offsetParent !== null) {
                startTour(launch.tourId);
                stripParam();
                return;
            }
            attempts += 1;
            if (attempts < maxAttempts) {
                window.setTimeout(tick, 100);
            } else {
                stripParam();
            }
        };

        const initial = window.setTimeout(tick, 150);
        return () => {
            cancelled = true;
            window.clearTimeout(initial);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, location.search, isNarrow]);

    React.useEffect(() => {
        if (!status?.eligible) {
            previousCompletedIdsRef.current = null;
            return;
        }

        const currentCompletedIds = getCompletedIds(status);
        const previousCompletedIds = previousCompletedIdsRef.current;
        if (previousCompletedIds) {
            const newlyCompleted = ITEMS.find(
                (item) =>
                    currentCompletedIds.has(item.id) &&
                    !previousCompletedIds.has(item.id),
            );
            if (newlyCompleted) {
                setJustDoneId(newlyCompleted.id);
                trackEvent("onboarding checklist item completed", {
                    item: newlyCompleted.id,
                });
                window.setTimeout(() => setJustDoneId(null), JUST_DONE_MS);
            }

            // AIDEV-NOTE: "Completed" tracks REQUIRED items only — optional items
            // (live call) don't block the all-set state, matching the widget.
            const requiredDone = (ids: ReadonlySet<OnboardingItemId>) =>
                REQUIRED_ITEM_IDS.every((id) => ids.has(id));
            if (
                !trackedCompletedRef.current &&
                requiredDone(currentCompletedIds) &&
                !requiredDone(previousCompletedIds)
            ) {
                trackedCompletedRef.current = true;
                trackEvent("onboarding checklist completed", {});
            }
        }

        previousCompletedIdsRef.current = currentCompletedIds;
    }, [status]);

    if (
        hidden ||
        !shouldShowOnboardingChecklist(location.pathname, location.search) ||
        !isPractitioner ||
        !status ||
        !status.eligible ||
        status.completedSeenAt
    ) {
        return null;
    }

    const completedIds = getCompletedIds(status);
    const callUrl = getBrowserEnvVar("ONBOARDING_CALL_URL") ?? null;

    const handleModeChange = (nextMode: OnboardingChecklistMode) => {
        setMode(nextMode);
        writeStoredMode(nextMode);
        trackEvent(
            nextMode === "expanded"
                ? "onboarding checklist expanded"
                : "onboarding checklist collapsed",
            {},
        );
    };

    // status is narrowed to the eligible shape by the early return above.
    const consultationSessionId = status.consultationSessionId;

    const handleItemClick = (id: OnboardingItemId) => {
        trackEvent("onboarding checklist item clicked", { item: id });

        if (id === "help") {
            if (isNarrow) {
                navigate("/help");
            } else {
                startTour("help-center-callout");
            }
            return;
        }

        if (id === "consult") {
            navigate(
                isNarrow
                    ? "/practitioner/sessions"
                    : `/practitioner/sessions?${TOUR_PARAM}=consult`,
            );
            return;
        }

        if (id === "chat") {
            const base = consultationSessionId
                ? `/consultation/${consultationSessionId}`
                : "/practitioner/sessions";
            navigate(
                !isNarrow && consultationSessionId
                    ? `${base}?${TOUR_PARAM}=chat`
                    : base,
            );
            return;
        }

        navigate(ITEM_ROUTES[id]);
    };

    const handleClose = () => {
        closeMutation.mutate(undefined, {
            onSuccess: () => {
                setHidden(true);
                trackEvent("onboarding checklist closed", {});
            },
        });
    };

    return (
        <OnboardingChecklistWidget
            completedIds={completedIds}
            justDoneId={justDoneId}
            mode={mode}
            onModeChange={handleModeChange}
            onItemClick={handleItemClick}
            onClose={handleClose}
            callUrl={callUrl}
            narrow={isNarrow}
            rightOffset={rightOffset}
            onCallClick={() => trackEvent("onboarding call link clicked", {})}
        />
    );
}
